import { Delete } from '@mui/icons-material';
import {
  DialogActions,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { blue } from '@mui/material/colors';
import { getClientsBankAccounts, savePayments } from 'lib/api/confirming';
import { getCompanyPayoutGroup } from 'lib/api/payments';
import BankAccountDialog from 'lib/common/BankAccountDialog';
import NomineeExporter from 'lib/common/NomineeExporter';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import EnhancedTable from 'lib/designSystem/Table';
import {
  formatMoney,
  showAlert,
  validatePermissions,
} from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

const nomineeHedearsForDb = [
  { key: 'bankAccountOriginName', label: 'Banco origen' },
  { key: 'bankAccountDestinationName', label: 'Banco destino' },
  { key: 'bankAccountDestinationNumber', label: 'N° Cuenta' },
  { key: 'bankAccountDestinationAccountType', label: 'Tipo de cuenta' },
  { key: 'bankAccountDestinationHolderName', label: 'Titular' },
  { key: 'bankAccountDestinationHolderIdentifier', label: 'Rut' },
  { key: 'amount', label: 'Monto', type: 'money' },
  { key: 'executedAt', label: 'Ejecutado', type: 'date' },
];

export const paymentsCompressor = (payments: any) => {
  const hashMap: { [key: string]: { payment: any; count: number } } = {};
  for (let i = 0; i < payments.length; i++) {
    const payment = payments[i];
    const key = `${payment.bankAccountDestinationId}-${payment.bankAccountOriginId}-${payment.amount}`;
    if (!hashMap[key]) {
      hashMap[key] = { payment, count: 1 };
    } else {
      hashMap[key].count += 1;
    }
  }
  return hashMap;
};

const newPaymentGrouped = {
  bankAccountDestinationId: 0,
  bankAccountOriginId: 0,
  amount: 0,
  cut: '',
};

const availableCuts = (amount: number) => [
  { id: 7000000, name: '$7.000.000' },
  { id: 5000000, name: '$5.000.000' },
  { id: amount, name: 'Sin corte' },
];

const PaymentModule = (props: any) => {
  const { selectedRequest, setOpen } = props;
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const [originAccounts, setOriginAccounts] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [previousPayments, setPreviousPayments] = useState<any[]>([]);
  const [exisitingNominee, setExisitingNominee] = useState<any>(false);
  const [openExporter, setOpenExporter] = useState(false);
  const [clients, setClients] = useState<any>([]);
  const [isEditing, setIsEditing] = useState(false);

  const [openBankAccountModal, setOpenBankAccountModal] = useState(false);
  const [
    businessIdentifierForAccountModal,
    setBusinessIdentifierForAccountModal,
  ] = useState('');

  const handleCreateNominee = async () => {
    setLoading(true);
    try {
      const compressedPayments = paymentsCompressor(payments);
      await savePayments(dispatch, {
        compressedPayments,
        id: selectedRequest.id,
      });
      showAlert(dispatch, 'success', 'Nómina guardada correctamente');
      setIsEditing(false);
      await setUpPayments();
    } catch (error: any) {
      console.error(error.response.data);
      if (error.response.data) {
        showAlert(dispatch, 'error', error.response.data.message);
      } else {
        showAlert(dispatch, 'error', 'Error desconocido al guardar la nómina');
      }
    }
    setLoading(false);
  };

  const setUpPayments = async () => {
    setLoading(true);
    try {
      const exisitingNominee = await getCompanyPayoutGroup(
        dispatch,
        'confirming-request',
        selectedRequest.id,
      );
      setPayments(exisitingNominee.payments);
      if (exisitingNominee?.payments?.length) setExisitingNominee(true);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al obtener la nómina');
    }
    setLoading(false);
  };

  const handleExport = () => {
    setOpenExporter(true);
  };

  const handleOpenBankAccountModal = (businessIdentifier: string) => {
    setBusinessIdentifierForAccountModal(businessIdentifier);
    setOpenBankAccountModal(true);
  };

  const handleGenerateNominee = () => {
    setLoading(true);
    // Revisar que todos los payment groups de cada client tengan los 4 campos completos
    const clientsWithAllFields = clients.filter((client: any) =>
      client.paymentGroups.every((paymentGroup: any) =>
        Object.values(paymentGroup).every((value: any) => value !== ''),
      ),
    );
    if (clientsWithAllFields.length !== clients.length) {
      showAlert(dispatch, 'error', 'Faltan campos por completar');
      setLoading(false);
      return;
    }
    const paymentGenerated = [];
    for (let i = 0; i < clients.length; i++) {
      const client = clients[i];
      const paymentGroup = client.paymentGroups;
      for (let j = 0; j < paymentGroup.length; j++) {
        const payment = paymentGroup[j];
        let remainingAmount = Number(payment.amount);
        const account = client.businessBankAccounts.find(
          (account: any) => account.id === payment.bankAccountDestinationId,
        );
        const originAccount = originAccounts.find(
          (account: any) => account.id === payment.bankAccountOriginId,
        );
        while (remainingAmount > 0) {
          const amountToDeposit =
            remainingAmount > payment.cut ? payment.cut : remainingAmount;
          paymentGenerated.push({
            businessDestinationName: client.businessName,
            bankAccountDestinationId: payment.bankAccountDestinationId,
            bankAccountOriginId: payment.bankAccountOriginId,
            bankAccountDestinationName: account.bankName,
            bankAccountDestinationNumber: account.accountNumber,
            bankAccountDestinationAccountType: account.accountType,
            bankAccountDestinationHolderName: account.holderName,
            bankAccountDestinationHolderIdentifier: account.holderIdentifier,
            bankAccountOriginName: originAccount.bankName,
            bankName: account.bankName,
            accountNumber: account.accountNumber,
            accountType: account.accountType,
            holderName: account.holderName,
            holderIdentifier: account.holderIdentifier,
            businessIdentifier: account.businessIdentifier,
            amount: amountToDeposit,
            operationId: selectedRequest.id,
            executedAt: null,
          });
          remainingAmount -= amountToDeposit;
        }
      }
    }
    setPayments(paymentGenerated);
    setLoading(false);
  };

  const handleAddTransfer = (businessIdentifier: string) => () => {
    setLoading(true);
    const clientToModify = clients.find(
      (client: any) => client.businessIdentifier === businessIdentifier,
    );
    clientToModify.paymentGroups = [
      ...(clientToModify.paymentGroups || []),
      structuredClone(newPaymentGrouped),
    ];
    setClients([...clients]);
    setLoading(false);
  };

  const handleAmountChange = (
    e: any,
    index: any,
    businessIdentifier: string,
  ) => {
    const clientToModify = clients.find(
      (client: any) => client.businessIdentifier === businessIdentifier,
    );
    const value = e.target.value.split('.').join('').split('$').join('');
    const newPayments = [...clientToModify.paymentGroups];
    newPayments[index].amount = parseInt(value);
    clientToModify.paymentGroups = newPayments;
    // Sumar el amount de todos los paymentGroups
    const totalAmount = clientToModify.paymentGroups.reduce(
      (acc: any, curr: any) => acc + curr.amount,
      0,
    );
    clientToModify.amountNominee = totalAmount;
    setClients([...clients]);
  };

  const fetchClientsBankAccounts = async (clientsInput?: any) => {
    const clientsForRequest = clientsInput || clients;
    const clientsWithBankAccounts = await getClientsBankAccounts(dispatch, {
      clientsForRequest,
    });
    if (clientsWithBankAccounts) {
      setOriginAccounts(clientsWithBankAccounts.ownBankAccounts);
      setClients(clientsWithBankAccounts.clientsBankAccounts);
    }
  };

  const setUpDataForClients = async () => {
    setLoading(true);
    await setUpPayments();
    const documents = selectedRequest.documents;
    const clients = Object.values(
      documents.reduce((acc: any, curr: any) => {
        const id = curr.businessIdentifier;
        if (!acc[id]) {
          acc[id] = {
            businessIdentifier: id,
            businessName: curr.businessName,
            totalAmount: 0,
          };
        }
        acc[id].totalAmount += curr.amount;
        return acc;
      }, {}),
    );
    setClients(clients);
    await fetchClientsBankAccounts(clients);
    setLoading(false);
  };

  useEffect(() => {
    void setUpDataForClients();
  }, []);

  const disableGenerate =
    !clients.length ||
    !clients.every((client: any) => client.businessBankAccounts) ||
    !clients.every(
      (client: any) => client.amountNominee === client.totalAmount,
    );

  const handleEditNominee = () => {
    setPreviousPayments([...payments]);
    setIsEditing(true);
    setPayments([]);
  };

  const somePaymentExecuted = payments.some((x) => x.executedAt);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <div>
      {!payments?.length ? (
        <div>
          <div
            style={{
              padding: '20px 0px',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography fontWeight={600} variant="h6">
              Generación nómina
            </Typography>
            <CustomButton
              color="primary"
              onClick={handleGenerateNominee}
              disabled={disableGenerate}
            >
              Generar nómina
            </CustomButton>
          </div>
          {!originAccounts.length && (
            <Typography variant="body1" padding="20px 0px">
              No tienes cuentas registradas para tu empresa. Crea una para poder
              generar una nómina.
            </Typography>
          )}
          {originAccounts.length &&
            clients.map((client: any) => (
              <div key={client.businessIdentifier}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <div
                    style={{
                      padding: '20px 0px',
                      display: 'flex',
                      gap: '20px',
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      {client.businessName}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      Monto solicitud: {formatMoney(client.totalAmount)}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      Monto en nómina: {formatMoney(client.amountNominee || 0)}
                    </Typography>
                  </div>
                  {client?.businessBankAccounts?.length > 0 && (
                    <div>
                      <CustomButton
                        color="primary"
                        onClick={handleAddTransfer(client.businessIdentifier)}
                      >
                        Agregar
                      </CustomButton>
                    </div>
                  )}
                </div>
                {client?.businessBankAccounts?.length == 0 && (
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <Typography variant="body1" padding="20px 0px">
                      No tienes cuentas registradas para este cliente. Crea una
                      para poder generar una nómina.
                    </Typography>
                    <Typography
                      variant="body1"
                      padding="20px 0px"
                      color={blue[900]}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                      onClick={() => {
                        handleOpenBankAccountModal(client.businessIdentifier);
                      }}
                    >
                      Acá
                    </Typography>
                  </div>
                )}
                {client?.businessBankAccounts?.length > 0 &&
                  client?.paymentGroups?.map((x: any, index: any) => (
                    <Grid
                      key={`${index}-${client.businessIdentifier}`}
                      container
                      spacing={2}
                      alignItems="center"
                      rowSpacing={4}
                      style={{ padding: '5px 0px' }}
                    >
                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          label="Monto"
                          value={formatMoney(
                            client.paymentGroups[index].amount,
                          )}
                          onChange={(e) =>
                            handleAmountChange(
                              e,
                              index,
                              client.businessIdentifier,
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel id="filter-label">
                            {'Cuenta Origen'}
                          </InputLabel>
                          <Select
                            labelId="filter-label"
                            id="Filtrar por estado"
                            fullWidth
                            value={
                              client.paymentGroups[index].bankAccountOriginId
                            }
                            onChange={(e) => {
                              const clientToModify = clients.find(
                                (clientFromClients: any) =>
                                  clientFromClients.businessIdentifier ===
                                  client.businessIdentifier,
                              );
                              const newPayments = [
                                ...clientToModify.paymentGroups,
                              ];
                              newPayments[index].bankAccountOriginId =
                                e.target.value;
                              clientToModify.paymentGroups = newPayments;
                              setClients([...clients]);
                            }}
                            label={'Cuenta bancaria'}
                          >
                            {originAccounts.map((x: any, index: any) => (
                              <MenuItem
                                key={`${index}-${client.businessIdentifier}`}
                                value={x.id}
                              >
                                {x.bankName} ({x.accountNumber})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel id="filter-label">
                            {'Cuenta destino'}
                          </InputLabel>
                          <Select
                            labelId="filter-label"
                            id="Filtrar por estado"
                            fullWidth
                            value={
                              client.paymentGroups[index]
                                .bankAccountDestinationId
                            }
                            onChange={(e) => {
                              const clientToModify = clients.find(
                                (clientFromClients: any) =>
                                  clientFromClients.businessIdentifier ===
                                  client.businessIdentifier,
                              );
                              const newPayments = [
                                ...clientToModify.paymentGroups,
                              ];
                              newPayments[index].bankAccountDestinationId =
                                e.target.value;
                              clientToModify.paymentGroups = newPayments;
                              setClients([...clients]);
                            }}
                            label={'Cuenta bancaria'}
                          >
                            {client?.businessBankAccounts?.map(
                              (x: any, index: any) => (
                                <MenuItem
                                  key={`${index}-${client.businessIdentifier}`}
                                  value={x.id}
                                >
                                  {x.bankName} ({x.accountNumber})
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <FormControl fullWidth>
                          <InputLabel id="filter-label">{'Corte'}</InputLabel>
                          <Select
                            labelId="filter-label"
                            id="Filtrar por estado"
                            fullWidth
                            value={client.paymentGroups[index].cut}
                            onChange={(e) => {
                              const clientToModify = clients.find(
                                (clientFromClients: any) =>
                                  clientFromClients.businessIdentifier ===
                                  client.businessIdentifier,
                              );
                              const newPayments = [
                                ...clientToModify.paymentGroups,
                              ];
                              newPayments[index].cut = e.target.value;
                              clientToModify.paymentGroups = newPayments;
                              setClients([...clients]);
                            }}
                            label={'Corte'}
                          >
                            {availableCuts(
                              client.paymentGroups[index].amount,
                            ).map((x: any, index: any) => (
                              <MenuItem
                                key={`${index}-${client.businessIdentifier}`}
                                value={x.id}
                              >
                                {x.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <Grid container>
                          <Grid item xs={9}>
                            <Typography textAlign={'center'}>
                              {client.paymentGroups[index].amount &&
                              client.paymentGroups[index].cut
                                ? ` N° de trasferencias: ${Math.ceil(
                                    client.paymentGroups[index].amount /
                                      client.paymentGroups[index].cut,
                                  )}`
                                : 'Complete los campos'}{' '}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <IconButton
                              onClick={() => {
                                const clientToModify = clients.find(
                                  (clientFromClients: any) =>
                                    clientFromClients.businessIdentifier ===
                                    client.businessIdentifier,
                                );
                                const newPayments = [
                                  ...clientToModify.paymentGroups,
                                ];
                                newPayments.splice(index, 1);
                                clientToModify.paymentGroups = newPayments;
                                setClients([...clients]);
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
              </div>
            ))}
        </div>
      ) : (
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 0px',
            }}
          >
            <Typography fontWeight={600} variant="h6">
              {'Nómina'}
            </Typography>
            {!exisitingNominee && (
              <CustomButton color="secondary" onClick={() => setPayments([])}>
                Volver
              </CustomButton>
            )}
            {exisitingNominee && (
              <div>
                <CustomButton color="primary" onClick={() => handleExport()}>
                  Exportar
                </CustomButton>
              </div>
            )}
          </div>
          <Typography variant="body1" padding="20px 0px">
            Acá podrás ver todas las transferencias que se realizarán en la
            nómina.
          </Typography>
          <EnhancedTable
            dataHeaders={nomineeHedearsForDb}
            data={payments}
            rowsPerPageDefault={10}
            finalSumRow
          />
          {exisitingNominee &&
            payments.every((x: any) => !x.executedAt) &&
            validatePermissions(user, 'financing_payout_view') && (
              <Typography variant="body1" padding="20px 0px">
                Una vez que de marquen todas como ejecutadas, la nómina se
                cerrará y la solicitud pasará a estado de activa. Para ejecutar
                los pagos, dirígete a{' '}
                <a href="/finance/payments/out">Pagos enviados</a>
              </Typography>
            )}
        </div>
      )}
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          Salir
        </CustomButton>
        {exisitingNominee && !isEditing && (
          <CustomButton
            color="primary"
            onClick={() => handleEditNominee()}
            disabled={somePaymentExecuted}
          >
            Editar nómina
          </CustomButton>
        )}
        {isEditing && (
          <CustomButton
            color="secondary"
            onClick={() => {
              setPayments([...previousPayments]);
              setIsEditing(false);
              setPreviousPayments([]);
            }}
          >
            Cancelar edición
          </CustomButton>
        )}
        {(!exisitingNominee && payments.length) ||
        (payments.length && isEditing) ? (
          <CustomButton
            color="primary"
            onClick={() => handleCreateNominee()}
            disabled={!payments.length}
          >
            Guardar Nómina
          </CustomButton>
        ) : null}
      </DialogActions>
      <NomineeExporter
        open={openExporter}
        setOpen={setOpenExporter}
        payments={payments}
        id={selectedRequest.id}
      />
      {openBankAccountModal && businessIdentifierForAccountModal && (
        <BankAccountDialog
          open={openBankAccountModal}
          setOpen={setOpenBankAccountModal}
          businessId={businessIdentifierForAccountModal}
          handleSuccessExit={setUpDataForClients}
        />
      )}
    </div>
  );
};

export default PaymentModule;
